import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { OverviewAPI } from 'api';
import { JobTableType } from 'api/jobApi';
import { buildJobListUrl } from '../jobListUrlParams';
import { StalledJob } from 'api/overviewApi';
import { getStatusColor } from 'util/statusColors';
import {
  cardSx,
  chartTokens,
  footerSx,
  formatCount,
  formatDuration,
  subtitleSx,
  tabularNumSx,
  titleSx,
} from './dashboardChartTokens';

/** Rows shown before the "View all jobs" hand-off. */
const ROW_LIMIT = 5;

const StatusBadge = ({ status }: { status: StalledJob['currentStatus'] }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      height: 22,
      paddingX: '8px',
      borderRadius: '999px',
      backgroundColor: getStatusColor(status, 100),
      color: getStatusColor(status, 800),
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
    {status}
  </Box>
);

interface Props {
  /** Narrows the queue to jobs where this user is the PM or supervisor. */
  coordinatorId?: string;
}

const NeedsAttentionCard = ({ coordinatorId }: Props) => {
  const navigate = useNavigate();
  const { isFetching, data } = useQuery(
    ['stalled-jobs', ROW_LIMIT, coordinatorId],
    () => OverviewAPI.fetchStalledJobs(ROW_LIMIT, coordinatorId)
  );

  const rows = data?.jobs ?? [];
  const total = data?.total ?? rows.length;
  const scaleMax = Math.max(...rows.map((row) => row.hoursSinceActive), 1);
  // coordinatorId is only ever set to narrow the queue to one person's own
  // jobs (see PMGridItems/TechnicianGridItems) — its absence always means
  // this is the organization-wide queue.
  const scopeLabel = coordinatorId ? 'your jobs' : 'across the organization';

  return (
    <Card sx={cardSx}>
      {isFetching && <LinearProgress />}
      <CardContent
        sx={{
          flex: 1,
          padding: { xs: 2.5, md: 3 },
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}>
          <Box>
            <Typography sx={titleSx}>Needs Attention</Typography>
            <Typography sx={subtitleSx}>
              {`${formatCount(total)} job${total === 1 ? '' : 's'} stalled · ${scopeLabel} · most inactive first`}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: chartTokens.divider, marginY: 2.5 }} />

        {isFetching && !rows.length ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: ROW_LIMIT }).map((_, index) => (
              <Skeleton key={index} height={56} />
            ))}
          </Box>
        ) : !rows.length ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <img
              alt="blank canvas"
              src="/images/undraw_blank_canvas_3rbb.svg"
              style={{ width: '60%', maxWidth: 220 }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {rows.map((row) => (
              <Box
                key={row.jobId}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/jobs/${row.jobId}`)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(`/jobs/${row.jobId}`);
                  }
                }}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'minmax(0, 1fr)',
                    sm: 'minmax(0, 1fr) 120px',
                  },
                  alignItems: 'center',
                  columnGap: 2,
                  padding: 1.5,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: `1px solid ${chartTokens.divider}`,
                  '&:hover': { backgroundColor: chartTokens.accentMuted },
                }}>
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      marginBottom: 0.5,
                    }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 14.5,
                        fontWeight: 700,
                        color: chartTokens.heading,
                      }}>
                      {data?.identifier ? `${data.identifier}-${row.jobNo}` : row.jobNo}
                    </Typography>
                    <StatusBadge status={row.currentStatus} />
                  </Box>
                  <Tooltip title={row.clientName}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 13,
                        color: chartTokens.body,
                      }}>
                      {row.clientName}
                    </Typography>
                  </Tooltip>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: chartTokens.muted,
                      marginTop: '2px',
                    }}>
                    {row.coordinatorName}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    gap: 0.5,
                  }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: chartTokens.heading,
                      ...tabularNumSx,
                    }}>
                    {formatDuration(row.hoursSinceActive)}
                  </Typography>
                  <Box
                    sx={{
                      width: { xs: '100%', sm: 120 },
                      height: 6,
                      borderRadius: '3px',
                      backgroundColor: chartTokens.track,
                      overflow: 'hidden',
                    }}>
                    <Box
                      sx={{
                        height: '100%',
                        width: `${(row.hoursSinceActive / scaleMax) * 100}%`,
                        minWidth: row.hoursSinceActive > 0 ? 4 : 0,
                        borderRadius: '3px',
                        backgroundColor: chartTokens.marker,
                        transition: 'width 300ms ease',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {Boolean(rows.length) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingTop: 2,
            }}>
            <Button
              disableRipple
              endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
              onClick={() =>
                navigate(
                  buildJobListUrl({
                    tab: JobTableType.OPEN,
                    // The queue is "stalled" work: inactive for 2+ days.
                    activity: ['H48_120', 'H120_PLUS'],
                    coordinator: coordinatorId ? [coordinatorId] : undefined,
                  })
                )
              }
              sx={{
                textTransform: 'none',
                fontSize: 13.5,
                fontWeight: 600,
                color: chartTokens.accent,
                padding: 0,
                minWidth: 0,
                '&:hover': { backgroundColor: 'transparent' },
              }}>
              View all stalled jobs
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default NeedsAttentionCard;
